import { createSlice } from '@reduxjs/toolkit';
import FORWARD_TARGET from '../../constants/ForwardTarget';

import { MESSAGE_SUB_TYPE } from '../../constants/MessageTypes';
import {
  calculatePersistedIdsByClientMessageId,
  removeMessageIds,
  replacePersistedPendingIds,
} from '../../helper/messagesHelpers';
import { channelMessagesUpsert } from '../messages/messagesSlice';

import { sendMessagesRollback } from './messageInputActions/sendMessagesOffline';
import { sendCurrentMessageState } from './messageInputThunks/sendCurrentMessageState';

const asyncState = {
  sendCurrentMessageState: {
    loading: 'idle',
    error: null,
  },
};

const initialState = {
  ...asyncState,
  channelInputs: {},
  stagedForwardRecipientsFromEtsChannelIds: [],
  etsMessage: {
    text: null,
    stagedTicketPart: null,
  },
};

const getChannelInputState = (state, channelId) => {
  if (!state.channelInputs[channelId]) {
    state.channelInputs[channelId] = {
      text: '',
      textPlaceholder: null,
      stagedProduct: null,
      stagedProcess: null,
      stagedReplyMessageId: null,
      stagedForwardMessageIds: [],
      stagedForwardRecipientsChannelIds: [], // channelIds to which messages get forwarded
      selectedMessageIds: [],
      selectedMessageIdsForwardingTarget: FORWARD_TARGET.CHAT,
      stagedMessageSubType: MESSAGE_SUB_TYPE.NONE,
      stagedFiles: [],
      stagedOrder: null,
    };
  }
  return state.channelInputs[channelId];
};

const clearChannelInputState = (channelInputState) => {
  // Note: In general we want to keep text for state reset
  // that's why it is not cleared here
  // ( e.g. channelInputReplyMessageStaged -> keep existing text)
  channelInputState.stagedProduct = null;
  channelInputState.stagedProcess = null;
  channelInputState.stagedReplyMessageId = null;
  channelInputState.stagedForwardMessageIds = [];
  channelInputState.selectedMessageIds = [];
  channelInputState.selectedMessageIdsForwardingTarget = FORWARD_TARGET.CHAT;
  channelInputState.stagedForwardRecipientsChannelIds = [];
  channelInputState.stagedMessageSubType = MESSAGE_SUB_TYPE.NONE;
  channelInputState.stagedFiles = [];
  channelInputState.stagedOrder = null;
};

const messageInputSlice = createSlice({
  name: 'messageInput',
  initialState,
  reducers: {
    channelInputTextChanged: (state, action) => {
      const { channelId, text, append } = action.payload;
      const channelInputState = getChannelInputState(state, channelId);
      if (append) {
        const current = channelInputState.text;
        channelInputState.text = current ? current + text : text;
      } else {
        channelInputState.text = text;
      }
    },
    channelInputFileStaged: (state, action) => {
      const { channelId, file } = action.payload;
      const channelInputState = getChannelInputState(state, channelId);
      channelInputState.stagedFiles.push(file);
    },
    channelInputFileUnstaged: (state, action) => {
      const { channelId, file } = action.payload;
      const channelInputState = getChannelInputState(state, channelId);
      channelInputState.stagedFiles = channelInputState.stagedFiles.filter((f) => {
        if ('path' in f) {
          return f.path !== file.path;
        }
        return f !== file;
      });
    },
    channelInputForwardRecipientChannelIdStaged: (state, action) => {
      const { channelId, recipientChannelId } = action.payload;
      const channelInputState = getChannelInputState(state, channelId);
      channelInputState.stagedForwardRecipientsChannelIds.push(recipientChannelId);
    },
    channelInputForwardRecipientChannelIdUnstaged: (state, action) => {
      const { channelId, recipientChannelId } = action.payload;
      const channelInputState = getChannelInputState(state, channelId);
      channelInputState.stagedForwardRecipientsChannelIds = channelInputState.stagedForwardRecipientsChannelIds.filter(
        (id) => id !== recipientChannelId,
      );
    },
    channelInputForwardRecipientsFromEtsChannelIdStaged: (state, action) => {
      const { recipientChannelId } = action.payload;
      state.stagedForwardRecipientsFromEtsChannelIds.push(recipientChannelId);
    },
    channelInputForwardRecipientsFromEtsChannelIdUnstaged: (state, action) => {
      const { recipientChannelId } = action.payload;
      state.stagedForwardRecipientsFromEtsChannelIds = state.stagedForwardRecipientsFromEtsChannelIds.filter(
        (id) => id !== recipientChannelId,
      );
    },
    channelInputReplyMessageStaged: (state, action) => {
      const { channelId, messageId } = action.payload;
      const channelInputState = getChannelInputState(state, channelId);
      clearChannelInputState(channelInputState);
      channelInputState.stagedReplyMessageId = messageId;
    },
    channelInputForwardMessagesStaged: (state, action) => {
      const { channelId, messageIds } = action.payload;
      const channelInputState = getChannelInputState(state, channelId);
      clearChannelInputState(channelInputState);
      channelInputState.stagedForwardMessageIds = messageIds;
    },
    channelInputProductStaged: (state, action) => {
      const { channelId, subType, text, product } = action.payload;
      const channelInputState = getChannelInputState(state, channelId);
      clearChannelInputState(channelInputState);
      channelInputState.stagedMessageSubType = subType;
      channelInputState.text = text;
      channelInputState.stagedProduct = product;
    },
    channelInputProcessStaged: (state, action) => {
      const { channelId, subType, text, process } = action.payload;
      const channelInputState = getChannelInputState(state, channelId);
      clearChannelInputState(channelInputState);
      channelInputState.stagedMessageSubType = subType;
      channelInputState.text = text;
      channelInputState.stagedProcess = process;
    },
    channelInputOrderStaged: (state, action) => {
      const { channelId, text, order, textPlaceholder } = action.payload;
      const channelInputState = getChannelInputState(state, channelId);
      clearChannelInputState(channelInputState);
      channelInputState.text = text;
      channelInputState.stagedOrder = order;
      channelInputState.textPlaceholder = textPlaceholder;
    },
    channelInputReset: (state, action) => {
      const { channelId } = action.payload;
      const channelInputState = getChannelInputState(state, channelId);
      channelInputState.text = '';
      clearChannelInputState(channelInputState);
    },
    resetEtsMessageState: (state) => {
      state.stagedForwardRecipientsFromEtsChannelIds = [];
      state.etsMessage.text = null;
      state.etsMessage.stagedTicketPart = null;
    },
    channelInputCleared: (state, action) => {
      const { channelId } = action.payload;
      const channelInputState = getChannelInputState(state, channelId);
      clearChannelInputState(channelInputState);
    },
    addSelectedMessageIds: (state, action) => {
      const { channelId, selectedMessageId, target = undefined } = action.payload;
      const channelInputState = getChannelInputState(state, channelId);
      channelInputState.selectedMessageIds = [...new Set([...channelInputState.selectedMessageIds, selectedMessageId])];
      if (target) {
        channelInputState.selectedMessageIdsForwardingTarget = target;
      }
    },
    removeSelectedMessageIds: (state, action) => {
      const { channelId, selectedMessageId } = action.payload;
      const channelInputState = getChannelInputState(state, channelId);
      channelInputState.selectedMessageIds = channelInputState.selectedMessageIds.filter(
        (id) => id !== selectedMessageId,
      );
    },
    resetSelectedMessageIds: (state, action) => {
      const { channelId } = action.payload;
      const channelInputState = getChannelInputState(state, channelId);
      channelInputState.selectedMessageIds = [];
    },
    messageStagedTicketPart: (state, action) => {
      const { ticketPart } = action.payload;
      state.etsMessage.stagedTicketPart = ticketPart;
    },
    etsMessageTicketPartTextChanged: (state, action) => {
      const { text, append } = action.payload;
      if (append) {
        const current = state.etsMessage.text;
        state.etsMessage.text = current ? current + text : text;
      } else {
        state.etsMessage.text = text;
      }
    },
  },
  extraReducers: {
    [sendCurrentMessageState.pending]: (state) => {
      state.sendCurrentMessageState.loading = 'pending';
    },
    [sendCurrentMessageState.fulfilled]: (state) => {
      state.sendCurrentMessageState.loading = 'idle';
    },
    [sendCurrentMessageState.rejected]: (state, action) => {
      state.sendCurrentMessageState.loading = 'idle';
      state.sendCurrentMessageState.error = action.error;
    },
    // Pending messages are removed from the messages state once persisted,
    // so references to them have to follow the persisted message
    [channelMessagesUpsert.type]: (state, action) => {
      const persistedIds = calculatePersistedIdsByClientMessageId(action.payload.channelMessages);
      for (const channelInputState of Object.values(state.channelInputs)) {
        const { selectedMessageIds, stagedForwardMessageIds, stagedReplyMessageId } = channelInputState;
        channelInputState.selectedMessageIds = replacePersistedPendingIds(selectedMessageIds, persistedIds);
        channelInputState.stagedForwardMessageIds = replacePersistedPendingIds(stagedForwardMessageIds, persistedIds);
        channelInputState.stagedReplyMessageId = persistedIds.get(stagedReplyMessageId) ?? stagedReplyMessageId;
      }
    },
    // Pending messages are removed from the messages state if sending them finally failed
    [sendMessagesRollback.type]: (state, action) => {
      const rolledBackIds = new Set(action.meta.messages.map((m) => m.clientMessageId));
      for (const channelInputState of Object.values(state.channelInputs)) {
        const { selectedMessageIds, stagedForwardMessageIds, stagedReplyMessageId } = channelInputState;
        channelInputState.selectedMessageIds = removeMessageIds(selectedMessageIds, rolledBackIds);
        channelInputState.stagedForwardMessageIds = removeMessageIds(stagedForwardMessageIds, rolledBackIds);
        if (rolledBackIds.has(stagedReplyMessageId)) {
          channelInputState.stagedReplyMessageId = null;
        }
      }
    },
  },
});

export const {
  channelInputReset,
  channelInputCleared,
  channelInputReplyMessageStaged,
  channelInputTextChanged,
  channelInputFileStaged,
  channelInputFileUnstaged,
  channelInputProductStaged,
  channelInputProcessStaged,
  channelInputForwardMessagesStaged,
  channelInputForwardRecipientChannelIdStaged,
  channelInputForwardRecipientChannelIdUnstaged,
  addSelectedMessageIds,
  removeSelectedMessageIds,
  resetSelectedMessageIds,
  channelInputOrderStaged,
  messageStagedTicketPart,
  etsMessageTicketPartTextChanged,
  channelInputForwardRecipientsFromEtsChannelIdStaged,
  channelInputForwardRecipientsFromEtsChannelIdUnstaged,
  resetEtsMessageState,
} = messageInputSlice.actions;
export default messageInputSlice.reducer;
